import { useCallback, useRef, useState } from 'react'
import {
    CTA_DEFAULT_FONT_SIZE,
    CTA_MIN_FONT_SIZE,
    findFontSizeToFit,
    getInnerWidth,
} from '../utils/fitToWidth'
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect'
import { useObservedLayoutMeasure } from './useObservedLayoutMeasure'

type UseFitToWidthOptions = {
    defaultFontSize?: number
    minFontSize?: number
    enabled?: boolean
    remeasureKey?: string | number | boolean | null | object
}

export type FitToWidthResult = {
    containerRef: React.RefObject<HTMLDivElement>
    contentRef: React.RefObject<HTMLSpanElement>
    fontSize: number
}

export function useFitToWidth(
    options: UseFitToWidthOptions = {},
): FitToWidthResult {
    const {
        defaultFontSize = CTA_DEFAULT_FONT_SIZE,
        minFontSize = CTA_MIN_FONT_SIZE,
        enabled = true,
        remeasureKey,
    } = options

    const containerRef = useRef<HTMLDivElement>(null)
    const contentRef = useRef<HTMLSpanElement>(null)
    const [fontSize, setFontSize] = useState(defaultFontSize)

    const measure = useCallback(() => {
        const container = containerRef.current
        const content = contentRef.current
        if (!enabled || !container || !content) {
            return
        }

        const innerWidth = getInnerWidth(container)
        if (innerWidth <= 0) {
            return
        }

        const nextFontSize = findFontSizeToFit({
            innerWidth,
            defaultFontSize,
            minFontSize,
            measureAtFontSize: (size) => {
                container.style.fontSize = `${size}px`
                return content.scrollWidth
            },
        })

        container.style.fontSize = `${nextFontSize}px`
        setFontSize((prev) => (prev === nextFontSize ? prev : nextFontSize))
    }, [defaultFontSize, enabled, minFontSize])

    useIsomorphicLayoutEffect(() => {
        if (!enabled) {
            const container = containerRef.current
            if (container) {
                container.style.fontSize = ''
            }
            setFontSize(defaultFontSize)
        }
    }, [defaultFontSize, enabled])

    useObservedLayoutMeasure({
        containerRef,
        contentRef,
        depKey: remeasureKey,
        enabled,
        measure,
    })

    return { containerRef, contentRef, fontSize }
}
